import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Theme } from '../../../../stores/types/themes';
import { GreenroomState } from '../../../../stores/types/types';
import { clearFontUploadAlert, saveFontAsset } from '../../../../stores/actions/font-assets';
import { FontAsset, KeyedFontAsset } from '../../../../stores/types/font-assets';
import SelectSmall from '../../../general-components/select-small';
import { SelectGroupOption } from '../../../general-components/select-group';
import useDynamicFonts, { getFontClassName } from '../../../../hooks/use-dynamic-fonts';
import Icon, { COLORS, ICONS } from '../../../general-components/icon';
import { showAlert } from '../../../general-components/alert-service';
import { Field } from '../../../types/stylepacks';

const buildFontAssetOption = (font: FontAsset): SelectGroupOption => {
	const nValue = {
		label: font.name,
		value: font,
		className: getFontClassName(font)
	}
	return nValue;
}

interface Props {
	theme: Theme | null,
	loadingThemeFonts: boolean,
	onSelect: (f: FontAsset, key: string) => void,
	resetFonts: () =>void,
	selectedFonts: KeyedFontAsset,
	fontFields: Field[],
	hasEdits: boolean,
}

interface UploadingData {
	fieldKey: string, // font-1, font-2...
	lastUploadedId: number // when this mismatches the app knows to update to new one
}

export default function FontsTab(props: Props) {
	const {onSelect, selectedFonts, fontFields, resetFonts, hasEdits} = props;
	const dispatch = useDispatch();
	const meeting = useSelector((s: GreenroomState) => s.SessionReducer.meeting);
	const fonts = useSelector((s: GreenroomState) => s.FontAssetsReducer.allFontAssets)
	const fontUploadAlert = useSelector((s: GreenroomState) => s.FontAssetsReducer.fontUploadAlert)
	const lastUploadedFont = useSelector((s: GreenroomState) => s.FontAssetsReducer.lastUploadedFont)
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [uploadData, setUploadData] = useState<UploadingData | null>(null);
	useDynamicFonts({ fonts });

	const options = useMemo((): SelectGroupOption[] => {
		const fontOptions: SelectGroupOption[] = fonts.map(buildFontAssetOption);

		fontOptions.push({
			label: 'Add new',
			className: 'text-gray-100 bl-font-l2',
			value: {id: 0, url: '', name:''},
			icon: ICONS.ADD,
			iconColor: COLORS.TEXT_GRAY_100
		})

		return fontOptions;
	}, [fonts, getFontClassName, selectedFonts]);

	const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file || !meeting) {
			setUploadData(null);
			return;
		}

		dispatch(saveFontAsset(meeting.meeting, file));
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const clickFontRow = (font: FontAsset, fieldKey: string, currentFont: FontAsset) => {
		if (currentFont && font.url === currentFont.url) {
			// do not set if selected matches current
			return;
		}
		if (font.url && font.id) {
			onSelect(font, fieldKey);
		} else {
			// add new
			setUploadData({fieldKey, lastUploadedId: lastUploadedFont?.id ?? 1});
			fileInputRef.current?.click();
		}	
	}

	useEffect(() => {
		if (!uploadData || !lastUploadedFont) return;
		if (lastUploadedFont.id !== uploadData.lastUploadedId) {
			// if the uploaded font changes and we have upload data that
			// is not that font, update selection with new font
			onSelect(lastUploadedFont, uploadData.fieldKey)
		}
	}, [lastUploadedFont?.id, uploadData]);

	useEffect(() => {
		if (fontUploadAlert) {
			showAlert(fontUploadAlert);
		}
		return () => {
			dispatch(clearFontUploadAlert())
		}
	}, [fontUploadAlert])

	if (props.loadingThemeFonts) {
		return null;
	}

	return (<>
		<div className="font-preview-container">
			<button className={`fonts-tab-reset-button ${hasEdits ? '' : 'no-edits-disabled'}`}  onClick={resetFonts} disabled={!hasEdits}>
				<Icon name={ICONS.REFRESH} color={COLORS.TEXT_GRAY_200} size={12}/>
				<span className='bl-font-l2'>Reset</span>
			</button> 
			<div className="font-preview-text">
				{Object.entries(selectedFonts).map(([fieldKey, font], i) => <span 
					key={fieldKey + font?.url}
					className={getFontClassName(font)}
				>
					{i > 0 ? ' + ' : ''}{font?.name}
				</span>)}
			</div>

			<div className="font-fields-container">
				{fontFields.map((field, index) => <SelectSmall
					key={`${index}-${field.userData}-${selectedFonts[field.userData]?.url ?? ''}`}
					onChange={(r) => clickFontRow(r, field.userData, selectedFonts[field.userData])}
					options={options}
					label={field.name}
					showLabel={true}
					selected={selectedFonts[field.userData] 
						? buildFontAssetOption(selectedFonts[field.userData]) 
						: options[0]}
					className='margin-top-28 margin-bottom-none font-field-dropdown'
					hasCarrot={true}
				/>)}
			</div>
		</div>

		<input
			ref={fileInputRef}
			type="file"
			accept=".ttf,.otf,.woff,.woff2"
			onChange={handleUpload}
			style={{ display: 'none' }}
		/>
	</>);
}
